"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.TextInput = void 0;
var _react = _interopRequireWildcard(require("react"));
var _useInputVariant = require("@cbhq/cds-common/hooks/useInputVariant");
var _useMergedRef = require("@cbhq/cds-common/hooks/useMergedRef");
var _usePrefixedId2 = require("@cbhq/cds-common/hooks/usePrefixedId");
var _useSpacingStyles = require("../hooks/useSpacingStyles");
var _HStack = require("../layout/HStack");
var _typography = require("../typography");
var _context = require("./context");
var _HelperText = require("./HelperText");
var _InputLabel = require("./InputLabel");
var _InputStack = require("./InputStack");
var _NativeInput = require("./NativeInput");
const _excluded = ["label", "accessibilityLabel", "helperText", "variant", "testID", "testIDMap", "start", "end", "width", "disabled", "align", "compact", "suffix", "onFocus", "onBlur", "borderRadius", "height", "inputNode", "bordered", "enableColorSurge", "helperTextErrorIconAccessibilityLabel"];
/* eslint-disable react/jsx-no-useless-fragment */
function _getRequireWildcardCache(nodeInterop) { if (typeof WeakMap !== "function") return null; var cacheBabelInterop = new WeakMap(); var cacheNodeInterop = new WeakMap(); return (_getRequireWildcardCache = function (nodeInterop) { return nodeInterop ? cacheNodeInterop : cacheBabelInterop; })(nodeInterop); }
function _interopRequireWildcard(obj, nodeInterop) { if (!nodeInterop && obj && obj.__esModule) { return obj; } if (obj === null || typeof obj !== "object" && typeof obj !== "function") { return { default: obj }; } var cache = _getRequireWildcardCache(nodeInterop); if (cache && cache.has(obj)) { return cache.get(obj); } var newObj = {}; var hasPropertyDescriptor = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var key in obj) { if (key !== "default" && Object.prototype.hasOwnProperty.call(obj, key)) { var desc = hasPropertyDescriptor ? Object.getOwnPropertyDescriptor(obj, key) : null; if (desc && (desc.get || desc.set)) { Object.defineProperty(newObj, key, desc); } else { newObj[key] = obj[key]; } } } newObj.default = obj; if (cache) { cache.set(obj, newObj); } return newObj; }
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
const TextInput = /*#__PURE__*/(0, _react.memo)( /*#__PURE__*/(0, _react.forwardRef)(function TextInput(_ref, ref) {
  var _testIDMap$end, _testIDMap$helperText, _testIDMap$label, _testIDMap$start;
  let label = _ref.label,
    accessibilityLabel = _ref.accessibilityLabel,
    _ref$helperText = _ref.helperText,
    helperText = _ref$helperText === void 0 ? '' : _ref$helperText,
    _ref$variant = _ref.variant,
    variant = _ref$variant === void 0 ? 'foregroundMuted' : _ref$variant,
    testID = _ref.testID,
    testIDMap = _ref.testIDMap,
    start = _ref.start,
    end = _ref.end,
    _ref$width = _ref.width,
    width = _ref$width === void 0 ? '100%' : _ref$width,
    _ref$disabled = _ref.disabled,
    disabled = _ref$disabled === void 0 ? false : _ref$disabled,
    _ref$align = _ref.align,
    align = _ref$align === void 0 ? 'start' : _ref$align,
    _ref$compact = _ref.compact,
    compact = _ref$compact === void 0 ? false : _ref$compact,
    _ref$suffix = _ref.suffix,
    suffix = _ref$suffix === void 0 ? '' : _ref$suffix,
    onFocus = _ref.onFocus,
    onBlur = _ref.onBlur,
    _ref$borderRadius = _ref.borderRadius,
    borderRadius = _ref$borderRadius === void 0 ? 'rounded' : _ref$borderRadius,
    height = _ref.height,
    inputNode = _ref.inputNode,
    _ref$bordered = _ref.bordered,
    bordered = _ref$bordered === void 0 ? true : _ref$bordered,
    _ref$enableColorSurge = _ref.enableColorSurge,
    enableColorSurge = _ref$enableColorSurge === void 0 ? false : _ref$enableColorSurge,
    _ref$helperTextErrorI = _ref.helperTextErrorIconAccessibilityLabel,
    helperTextErrorIconAccessibilityLabel = _ref$helperTextErrorI === void 0 ? 'error' : _ref$helperTextErrorI,
    htmlInputElmProps = _objectWithoutPropertiesLoose(_ref, _excluded);
  const _useState = (0, _react.useState)(false),
    focused = _useState[0],
    setFocused = _useState[1];
  const focusedVariant = (0, _useInputVariant.useInputVariant)(focused, variant);
  const internalRef = (0, _react.useRef)();
  const refs = (0, _useMergedRef.useMergedRef)(ref, internalRef);

  // Only generate a helperTextId if helperText is defined, otherwise
  // set it to undefined
  const shouldSetHelperTextId = (0, _react.useMemo)(() => helperText !== '', [helperText]);
  const shouldSetLabelId = label !== undefined;
  const _usePrefixedId = (0, _usePrefixedId2.usePrefixedId)(['cds-textinput-description', 'cds-textinput-label']),
    helperTextId = _usePrefixedId[0],
    labelId = _usePrefixedId[1];

  // TODO surface this as a prop
  const hasError = variant === 'negative';
  const handleOnFocus = (0, _react.useCallback)(e => {
    setFocused(true);
    onFocus === null || onFocus === void 0 ? void 0 : onFocus(e);
  }, [onFocus]);
  const handleOnBlur = (0, _react.useCallback)(e => {
    onBlur === null || onBlur === void 0 ? void 0 : onBlur(e);
    setFocused(false);
  }, [onBlur]);
  const handleNodePress = (0, _react.useCallback)(() => {
    var _internalRef$current;
    setFocused(true);
    (_internalRef$current = internalRef.current) === null || _internalRef$current === void 0 ? void 0 : _internalRef$current.focus();
  }, [setFocused, internalRef]);

  /**
   * If start exist, the padding
   * between input area and icon should be 0.5 (4px).
   * This is not the case when there is no start.
   * In normal circumstances, spacing horizontal should be 2 (16px)
   */
  const startSpacing = (0, _useSpacingStyles.useSpacingStyles)({
    spacingVertical: compact ? 1 : 2,
    spacingStart: 0.5,
    spacingEnd: compact ? 1 : 2
  });
  const inputNodeCloned = (0, _react.useMemo)(() => {
    /** Ensures that the renderedInput has the blurring, focusing, disabled features */
    if (inputNode) {
      const clonedElm = /*#__PURE__*/(0, _react.cloneElement)(inputNode, {
        onFocus: handleOnFocus,
        onBlur: handleOnBlur,
        ref: refs,
        'aria-describedby': shouldSetHelperTextId && helperTextId,
        'aria-invalid': hasError,
        id: shouldSetLabelId ? labelId : undefined,
        disabled
      });
      return clonedElm;
    }

    // By default, it will use the NativeInput
    return /*#__PURE__*/_react.default.createElement(_NativeInput.NativeInput, _extends({
      ref: refs,
      accessibilityHint: shouldSetHelperTextId ? helperTextId : undefined,
      accessibilityLabel: accessibilityLabel !== null && accessibilityLabel !== void 0 ? accessibilityLabel : label,
      align: align,
      "aria-invalid": hasError,
      compact: compact,
      containerSpacing: start ? startSpacing : undefined,
      disabled: disabled,
      id: shouldSetLabelId ? labelId : undefined,
      onBlur: handleOnBlur,
      onFocus: handleOnFocus,
      testID: testID
    }, htmlInputElmProps));
  }, [inputNode, align, accessibilityLabel, label, helperTextId, start, startSpacing, handleOnFocus, handleOnBlur, disabled, compact, testID, refs, hasError, htmlInputElmProps, shouldSetHelperTextId, shouldSetLabelId, labelId]);
  return /*#__PURE__*/_react.default.createElement(_context.TextInputFocusVariantContext.Provider, {
    value: focused ? focusedVariant : undefined
  }, /*#__PURE__*/_react.default.createElement(_InputStack.InputStack, {
    borderRadius: borderRadius,
    borderWidth: bordered ? 'input' : 'none'
    // If bordered is true, we want disableFocusedStyle = false
    // If bordered is false, we want disableFocusedStyle = true
    ,
    disableFocusedStyle: !bordered,
    disabled: disabled,
    enableColorSurge: enableColorSurge,
    endNode: (suffix !== '' || !!end) && /*#__PURE__*/_react.default.createElement(_HStack.HStack, {
      alignItems: "center",
      gap: 2,
      justifyContent: "center",
      onClick: handleNodePress,
      testID: (_testIDMap$end = testIDMap === null || testIDMap === void 0 ? void 0 : testIDMap.end) !== null && _testIDMap$end !== void 0 ? _testIDMap$end : ''
    }, suffix !== '' && /*#__PURE__*/_react.default.createElement(_typography.TextLabel1, {
      as: "p",
      color: "foregroundMuted",
      spacingEnd: 2
    }, suffix), !!end && /*#__PURE__*/_react.default.createElement(_react.default.Fragment, null, end)),
    focused: focused,
    height: height,
    helperTextNode: !!helperText && (typeof helperText === 'string' ? /*#__PURE__*/_react.default.createElement(_HelperText.HelperText, {
      accessibilityLabel: helperText,
      align: align,
      color: variant,
      errorIconAccessibilityLabel: helperTextErrorIconAccessibilityLabel,
      errorIconTestID: `${testIDMap === null || testIDMap === void 0 ? void 0 : testIDMap.helperText}-error-icon`,
      id: shouldSetHelperTextId ? helperTextId : undefined,
      testID: (_testIDMap$helperText = testIDMap === null || testIDMap === void 0 ? void 0 : testIDMap.helperText) !== null && _testIDMap$helperText !== void 0 ? _testIDMap$helperText : ''
    }, helperText) : helperText),
    inputNode: inputNodeCloned,
    labelNode: !compact && !!label && /*#__PURE__*/_react.default.createElement(_InputLabel.InputLabel, {
      htmlFor: shouldSetLabelId ? labelId : undefined,
      testID: (_testIDMap$label = testIDMap === null || testIDMap === void 0 ? void 0 : testIDMap.label) !== null && _testIDMap$label !== void 0 ? _testIDMap$label : ''
    }, label),
    startNode: (compact || !!start) && /*#__PURE__*/_react.default.createElement(_HStack.HStack, {
      alignItems: "center",
      gap: 2,
      justifyContent: "center",
      onClick: handleNodePress,
      testID: (_testIDMap$start = testIDMap === null || testIDMap === void 0 ? void 0 : testIDMap.start) !== null && _testIDMap$start !== void 0 ? _testIDMap$start : ''
    }, compact && !!label && /*#__PURE__*/_react.default.createElement(_InputLabel.InputLabel, {
      htmlFor: shouldSetLabelId ? labelId : undefined,
      spacingStart: 2
    }, label), !!start && /*#__PURE__*/_react.default.createElement(_react.default.Fragment, null, start)),
    variant: variant,
    width: width
  }));
}));
exports.TextInput = TextInput;